/**
 * Peças usadas por todos os outros esquemas.
 *
 * Regra de convivência com o motor: identificadores criados pelo servidor são
 * ULIDs (API 1), mas identificadores de clube, jogador e proposta nascem dentro
 * do mundo do motor e são curtos, do tipo `c_1` ou `p_12`. Por isso o esquema
 * geral de identificador aceita qualquer texto opaco curto, e `UlidSchema` fica
 * disponível para as rotas que só recebem identificadores do servidor.
 */
import { z } from "zod";
import { DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_MIN_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, } from "../constants.js";
/** Identificador opaco de qualquer entidade do protocolo. */
export const IdSchema = z.string().min(1).max(64);
/**
 * ULID em base32 de Crockford, que é o formato dos identificadores criados pelo
 * servidor. As letras I, L, O e U ficam de fora do alfabeto.
 */
export const UlidSchema = z
    .string()
    .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, "Identificador fora do formato ULID.");
/** Data e hora em ISO 8601, sempre em UTC (API 1, SERVER 8). */
export const IsoDateTimeSchema = z.string().datetime();
/** Endereço de e-mail normalizado. */
export const EmailSchema = z.string().trim().toLowerCase().email().max(254);
/** Senha em texto puro, só na entrada das rotas de autenticação. */
export const PasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
/** Nome visível do usuário. */
export const DisplayNameSchema = z
    .string()
    .trim()
    .min(DISPLAY_NAME_MIN_LENGTH)
    .max(DISPLAY_NAME_MAX_LENGTH);
/** Idioma do usuário, no formato `pt-BR`. */
export const LocaleSchema = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);
/** Código de país em duas letras, como aparece em `LeagueSettings.countries`. */
export const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/);
/** Temporada do mundo, contada a partir de zero. */
export const SeasonSchema = z.number().int().min(0);
/** Rodada do calendário, contada a partir de zero. */
export const RoundSchema = z.number().int().min(0);
/** Valor em dinheiro. Nunca negativo em entrada de decisão. */
export const MoneySchema = z.number().finite().nonnegative();
/**
 * Plataforma de um dispositivo.
 *
 * Mesmos nomes que o cliente já reporta ao GlitchTip (GLITCHTIP 4) e que a
 * tabela `push_tokens` guarda (DATABASE 2.9), com `desktop` para as builds
 * Tauri.
 */
export const PlatformSchema = z.enum(["web", "android", "ios", "desktop"]);
/** Dificuldade do mundo. Mesmos valores do motor. */
export const DifficultySchema = z.enum(["facil", "normal", "dificil"]);
//# sourceMappingURL=common.js.map